import { useEffect, useRef, useState, FC } from 'react';
import { Box } from "@chakra-ui/react"
import { usePack, usePackAssets, useToastNotification } from "lib/hooks";
import { DropFileInProgress } from "./dropFileInProgress";
import { RowChildDropzone, TopLevelDropzone, getDropOrderPosition } from "domains/drag-and-drop";
import { useUploadFiles } from 'lib/hooks';
import { usePackContext } from "domains/pack";

type Props = {
  path: string,
  groupId?: string;
  rowId?: string;
};

enum Events {
  dragenter = 'dragenter',
  dragleave = 'dragleave',
  dragover = 'dragover',
  drop = 'drop',
}

// TODO: Move to utils
const addEventListeners = (events: Events[], node: HTMLElement, cb: (e?: Event) => void) => {
  events.forEach((event) => {
    node.addEventListener(event, cb, false);
  })
}

// TODO: Move to utils
const removeEventListeners = (events: Events[], node: HTMLElement, cb: (e?: Event) => void) => {
  events.forEach((event) => {
    node.removeEventListener(event, cb, false);
  })
}

export const DropFile: FC<Props> = ({ path, groupId = '', rowId = '' }) => {
  const ref = useRef(null);
  const { packId } = usePackContext();
  const { data: pack } = usePack(packId);
  const { data } = usePackAssets(pack);
  const [isOver, setIsOver] = useState<boolean>(false);
  const { errorToast } = useToastNotification();
  const { uploadFiles, percent } = useUploadFiles(packId);

  useEffect(() => {
    if (!ref.current) return;

    const node = ref.current;

    const onDragEnter = (event): void => {
      event.preventDefault();
      event.stopPropagation();

      if (!isOver) {
        setIsOver(true);
      }
    }

    const onDragLeave = (event): void => {
      if (isOver && (event.currentTarget === event.target || !event.currentTarget.contains(event.relatedTarget))) {
        setIsOver(false);
      }
    }

    const onDrop = async (event: DragEvent): Promise<void> => {
      event.preventDefault();
      event.stopPropagation();

      const index: any = path.length > 1 ? path.split('-') : [ path ];
      let order = getDropOrderPosition(data, index);

      let position: any = {
        order,
      }

      if (groupId) {
        position.group_id = groupId;
      }

      if (rowId) {
        position.row_id = rowId;
      }

      await uploadFiles(event.dataTransfer.files, position);
      setIsOver(false);
    }

    addEventListeners([Events.dragenter, Events.dragover], node, onDragEnter);
    addEventListeners([Events.dragleave], node, onDragLeave);
    addEventListeners([Events.drop], node, onDrop);

    return () => {
      removeEventListeners([Events.dragenter, Events.dragover], node, onDragEnter);
      removeEventListeners([Events.dragleave], node, onDragLeave);
      removeEventListeners([Events.drop], node, onDrop);
    }
  }, [isOver, pack, errorToast, data, path, groupId, rowId, uploadFiles]);

  const isChildDropZone = path.split('-').length === 3;

  return (
    <Box ref={ref} position={"relative"} height={(percent || isOver) ? '74px' : '15px'}>
      {!isChildDropZone
        ? <TopLevelDropzone isActive={isOver} ref={null}/>
        : <RowChildDropzone isActive={isOver} ref={null}/>
      }
      <DropFileInProgress percent={percent} />
    </Box>
  )
}
